import React, { useEffect, useState } from 'react';

export type PaginatorProps = {
    currentPage: number;
    lastPage: number;
    onPageChange: (page: number) => void;
    hideIf?: boolean;
};

function Paginator({ currentPage, lastPage, onPageChange, hideIf }: PaginatorProps) {
    const [pages, setPages] = useState<number[]>([]);
    const [selectedPage, setSelectedPage] = useState<number>(currentPage);

    useEffect(() => {
        setSelectedPage(currentPage);
        setPages(getCorrectPages(currentPage, lastPage, 2));
    }, [currentPage, lastPage]);

    function changePage(page: number, ignoreCurrent: boolean = false) {
        if (!(ignoreCurrent && page === currentPage)) {
            setSelectedPage(page);
            onPageChange(page);
        }
    }

    if (hideIf) {
        return <div />;
    }

    return (
        <nav aria-label="Pagination">
            <ul className="pagination">
                <Item action={() => changePage(1, true)}>
                    <i className="fas fa-angle-double-left" />
                </Item>
                <Item action={() => changePage(Math.max(currentPage - 1, 1), true)}>
                    <i className="fas fa-angle-left" />
                </Item>
                <Item showIf={pages[0] > 1}>...</Item>
                {pages.map((page) => (
                    <Item key={page} active={page === selectedPage} action={() => changePage(page)}>
                        {page}
                    </Item>
                ))}
                <Item showIf={pages[pages.length - 1] < lastPage}>...</Item>
                <Item action={() => changePage(Math.min(currentPage + 1, lastPage), true)}>
                    <i className="fas fa-angle-right" />
                </Item>
                <Item action={() => changePage(lastPage, true)}>
                    <i className="fas fa-angle-double-right" />
                </Item>
            </ul>
        </nav>
    );
}

type ItemProps = { action?: () => void; active?: boolean; showIf?: boolean; children: React.ReactNode };

function Item({ action, active, showIf = true, children }: ItemProps) {
    return showIf ? (
        <li className={`page-item ${active ? 'active' : ''}`}>
            <button type="button" className="page-link" onClick={action}>
                {children}
            </button>
        </li>
    ) : null;
}

function getCorrectPages(currentPage: number, lastPage: number, range: number) {
    const list = [...Array(1 + range * 2).keys()];
    const middlePage = Math.max(1 + range, Math.min(currentPage, lastPage - range));

    return list.map((n) => n + middlePage - range).filter((n) => n >= 1 && n <= lastPage);
}

export default Paginator;
